import { ExternalLink } from "lucide-react";
import type { FC } from "react";
import { Link } from "react-router-dom";
import type { MatchingResultListItem } from "../../../../hooks/use-matching-results-api";

type MatchingResultsTableProps = {
  results: MatchingResultListItem[];
};

const TRIGGER_TYPE_BADGE: Record<string, { label: string; className: string }> =
  {
    freetext: { label: "フリー", className: "badge-primary" },
    manual: { label: "手動", className: "badge-secondary" },
    preset: { label: "プリセット", className: "badge-accent" },
    auto: { label: "自動", className: "badge-info" },
  };

const SOURCE_TYPE_BADGE: Record<string, { label: string; className: string }> =
  {
    hr: { label: "人材", className: "text-blue-600 border-blue-300" },
    matter: { label: "案件", className: "text-green-600 border-green-300" },
  };

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
  new: { label: "新規", className: "badge-warning" },
  viewed: { label: "閲覧済", className: "badge-ghost" },
  proposed: { label: "提案済", className: "badge-success" },
  dismissed: { label: "却下", className: "badge-error" },
};

/**
 * total depth からバッジクラスを返す
 * @param {number} total total depth
 * @return {string} badge class
 */
function depthBadgeClass(total: number): string {
  if (total <= 2) return "badge-success";
  if (total === 3) return "badge-info";
  if (total === 4) return "badge-warning";
  return "badge-error";
}

/**
 * snapshot から total depth を計算する
 * @param {MatchingResultListItem} result マッチング結果
 * @return {number | null} total depth (算出不能時は null)
 */
function getTotalDepth(result: MatchingResultListItem): number | null {
  const isMatterSource = result.sourceType === "matter";
  const matterSnap = (
    isMatterSource ? result.sourceSnapshot : result.matchedSnapshot
  ) as Record<string, unknown> | null;
  const hrSnap = (
    isMatterSource ? result.matchedSnapshot : result.sourceSnapshot
  ) as Record<string, unknown> | null;
  const matterDepth = (matterSnap?.depth as number | null) ?? null;
  const hrDepth = (hrSnap?.depth as number | null) ?? null;
  if (matterDepth != null && hrDepth != null) return matterDepth + hrDepth;
  return null;
}

/**
 * スコアに応じたバッジクラスを返す
 * @param {number} score スコア (0〜1)
 * @return {string} badge class
 */
function scoreClass(score: number): string {
  if (score >= 0.8) return "text-success";
  if (score >= 0.6) return "text-warning";
  return "text-error";
}

/**
 * 日付を相対表示またはフォーマットする
 * @param {string} dateStr ISO日付文字列
 * @return {string} フォーマット済み日付
 */
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "今日";
  if (diffDays === 1) return "昨日";
  if (diffDays < 7) return `${diffDays}日前`;

  return `${date.getMonth() + 1}/${date.getDate()}`;
}

/**
 * 結果に対する導線リンクのパスを返す
 * @param {MatchingResultListItem} result マッチング結果
 * @return {string | null} リンクパス (null の場合はリンクなし)
 */
function getLinkPath(result: MatchingResultListItem): string | null {
  switch (result.triggerType) {
    case "manual":
    case "auto":
      return result.sourceEmailId
        ? `/emails/search/${result.sourceEmailId}`
        : null;
    case "preset":
      return result.matchedPresetId
        ? `/settings/presets/${result.matchedPresetId}`
        : null;
    default:
      return null;
  }
}

/**
 * マッチング結果テーブル
 * @param {MatchingResultsTableProps} props コンポーネントProps
 * @return {JSX.Element} MatchingResultsTable コンポーネント
 */
export const MatchingResultsTable: FC<MatchingResultsTableProps> = ({
  results,
}) => {
  if (results.length === 0) {
    return (
      <div className="text-center py-12 text-base-content/60">
        <p>マッチング結果がありません</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="table table-zebra">
        <thead>
          <tr>
            <th>マッチ元</th>
            <th>マッチ先</th>
            <th>種別</th>
            <th>ソース</th>
            <th>スコア</th>
            <th>商流</th>
            <th>ステータス</th>
            <th>日時</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {results.map(result => {
            const trigger = TRIGGER_TYPE_BADGE[result.triggerType] || {
              label: result.triggerType,
              className: "badge-ghost",
            };
            const source = SOURCE_TYPE_BADGE[result.sourceType] || {
              label: result.sourceType,
              className: "",
            };
            const status = STATUS_BADGE[result.status] || {
              label: result.status,
              className: "badge-ghost",
            };
            const linkPath = getLinkPath(result);
            const scorePercent = Math.round(result.totalScore * 100);

            return (
              <tr key={result.id}>
                <td className="max-w-48">
                  <span className="truncate block text-sm">
                    {result.sourceSubject ||
                      (result.sourceSnapshot as Record<string, string>)
                        ?.subject ||
                      "(フリーテキスト)"}
                  </span>
                </td>
                <td className="max-w-48">
                  <span className="truncate block text-sm">
                    {result.matchedSubject}
                  </span>
                </td>
                <td>
                  <span className={`badge badge-sm ${trigger.className}`}>
                    {trigger.label}
                  </span>
                </td>
                <td>
                  <span
                    className={`badge badge-sm badge-outline ${source.className}`}
                  >
                    {source.label}
                  </span>
                </td>
                <td>
                  <span
                    className={`font-mono text-sm font-semibold ${scoreClass(result.totalScore)}`}
                  >
                    {scorePercent}%
                  </span>
                </td>
                <td>
                  {(() => {
                    const total = getTotalDepth(result);
                    if (total == null) {
                      return (
                        <span className="badge badge-sm badge-ghost">
                          商流 --
                        </span>
                      );
                    }
                    return (
                      <span
                        className={`badge badge-sm ${depthBadgeClass(total)}`}
                      >
                        商流 {total}
                      </span>
                    );
                  })()}
                </td>
                <td>
                  <span className={`badge badge-sm ${status.className}`}>
                    {status.label}
                  </span>
                </td>
                <td className="text-sm text-base-content/60">
                  {formatDate(result.createdAt)}
                </td>
                <td>
                  {linkPath && (
                    <Link
                      to={linkPath}
                      className="btn btn-ghost btn-xs"
                      title="詳細を表示"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </Link>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
